import React from 'react';
import styled from 'styled-components';
import { Panel, Label, Link } from '@uhg-harmony/uitk-react';
import { Arrow } from 'builder/scenes/SiteBuilder/SettingsPanel/SettingsPanel';

import {
  SizeLocationData,
  WidgetSizeLocation,
} from '../../../builder/components/WidgetSizeLocation';
import { Config } from './types';

import {
  Background,
  BackgroundData,
} from 'builder/components/WidgetConfigurations/Background/Background';
import {
  Padding,
  PaddingData,
} from 'builder/components/WidgetConfigurations/Padding';
import { Border } from 'builder/components/WidgetConfigurations/Border';
import { SearchTypography } from './SearchTypography';
import { toast } from 'react-toastify';

export const SidebarNav = styled.nav`
  width: 365px;
  height: 80vh;
  display: flex;
  justify-content: center;
  position: fixed;
  top: 60px;
  transition: 850ms;
  z-index: 10;
  box-shadow: 0 3px 6px -4px #0000001f, 0 6px 16px #00000014,
    0 9px 28px 8px #0000000d;
`;

export const NavIcon = styled(Link)`
  font-size: 2rem;
  height: 40px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background: #cccac9;
  border-bottom: 1px #888888;
`;

export const SidebarWrap = styled.div`
  width: 100%;
`;

export const StyledPanelTitle = {
  'font-family': 'OptumSans',
  'font-size': '14px',
  color: '#1d1929',
  'font-weight': '700',
  'margin-left': '0.8em',
};

export const PanelTop = styled.div`
  .cGdfIs {
    padding: 0rem 2rem 3.5rem 1.5rem;
  }
`;

export const StyledPanel = styled.div`
  & > div {
    border-radius: 0;
  }
  & button[class^='Panelcomponent__HeaderSection'] {
    height: 2.5em;
    background: rgba(141, 145, 160, 0.07);
    border-radius: 0;
    &: hover,&: focus {
      box-shadow: none;
    }
  }
  & span[class^='Panelcomponent__HeaderMainContent'] {
    height: 2.5em;
  }
  & div[class^='uitk-panel Panelcomponent__Panel'] {
    margin-bottom: 0rem;
    border: none;
  }
  & div[class^='Panelcomponent__Content'] {
    padding: 1.5rem;
  }
`;

export const SizeLocationPanel = styled(Panel)`
  > div {
    padding: 1rem;
  }
`;

const SearchBarWidgetControl: React.FC<WidgetControlProps<Config>> = (
  props: WidgetControlProps<Config>
) => {
  const { value, onChange, widgetState, pages } = props;
  const handleLineColor = data => {
    onChange({
      ...value,
      lineColor: data,
    });
  };

  const handleBackgroundColor = (data: string) => {
    onChange({
      ...value,
      backgroundData: {
        ...value.backgroundData,
        color: data,
      },
    });
  };

  const updateWidgetSizeLoction = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };
  const updateBackgroundData = (data: BackgroundData) => {
    onChange({
      ...value,
      backgroundData: data,
    });
  };
  const updateBackgroundImage = (data: Image) => {
    onChange({
      ...value,
      backgroundImage: data,
    });
  };
  const updatePaddingData = (data: PaddingData) => {
    onChange({
      ...value,
      paddingData: data,
    });
  };

  const updateBorderData = (data: BorderData) => {
    onChange({
      ...value,
      borderData: data,
    });
  };
  const handleTextColor = (data: string) => {
    if (data.startsWith('linear') || data.startsWith('radial')) {
      toast('Gradient cannot be used for text color', {
        type: 'warning',
        theme: 'colored',
      });
    } else {
      onChange({
        ...value,
        textColor: data,
      });
    }
  };
  return (
    <>
      <Panel.Group>
        <StyledPanel>
          <Panel
            key={'typography-panel'}
            title={<Label style={StyledPanelTitle}>Typography</Label>}
            icon={Arrow}
            data-test-id="typography-panel"
          >
            <PanelTop>
              <SearchTypography
                color={widgetState.config.textColor}
                data-test-id="search-typography-component"
                widgetState={props.widgetState}
                handleFont={data => {
                  onChange({
                    ...value,
                    font: data,
                  });
                }}
                handleTextColor={handleTextColor}
                pages={pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
              />
            </PanelTop>
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <SizeLocationPanel
            key={'size-location-panel'}
            title={<Label style={StyledPanelTitle}>Size and Location</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="authentication-panel"
          >
            <WidgetSizeLocation
              data-test-id="widget-size-location"
              sizeLocation={{
                xLocation: value.xLocation,
                yLocation: value.yLocation,
                width: value.width,
                height: value.height,
                uniqueId: value.uniqueId,
              }}
              updateWidgetSizeLocation={updateWidgetSizeLoction}
              activeWidgetId={widgetState.id}
            />
          </SizeLocationPanel>
        </StyledPanel>
        <StyledPanel>
          <PanelTop>
            <Panel
              key={'text-background-panel'}
              title={<Label style={StyledPanelTitle}>Background</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="text-background-panel"
            >
              <Background
                data-test-id="background-component"
                backgroundData={updateBackgroundData}
                backgroundImage={updateBackgroundImage}
                widgetState={widgetState}
                handleLineColor={handleLineColor}
                handleBackgroundColor={handleBackgroundColor}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
              />
            </Panel>
          </PanelTop>
        </StyledPanel>
        <StyledPanel>
          <PanelTop>
            <Panel
              key={'border-panel'}
              title={<Label style={StyledPanelTitle}>Border</Label>}
              initiallyClosed
              icon={Arrow}
            >
              <Border
                data-test-id="border-data"
                updateBorderData={updateBorderData}
                handleLineColor={handleLineColor}
                widgetState={widgetState}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
              />
            </Panel>
          </PanelTop>
        </StyledPanel>
        <StyledPanel>
          <PanelTop>
            <Panel
              key={'padding-panel'}
              title={<Label style={StyledPanelTitle}>Padding</Label>}
              initiallyClosed
              icon={Arrow}
            >
              <Padding
                data-test-id="padding-data"
                paddingData={updatePaddingData}
                widgetState={widgetState}
              />
            </Panel>
          </PanelTop>
        </StyledPanel>
      </Panel.Group>
    </>
  );
};

export default SearchBarWidgetControl;
